---
title: "Saved searches"
description: "Every deep search you run is saved to a sidebar list in Chosen. How to re-open a saved search, rename it, delete it — and why a search is a query, not a frozen list."
category: "search"
keywords:
  - "saved searches"
  - "save candidate search"
  - "re-run search"
  - "saved search vs list"
  - "recurring candidate search"
schema: "HowTo"
nav:
  section: "guides"
  order: 40
  parent: "/guides/search"
---

Every AI deep search you run is saved automatically. It lands in a list in the left sidebar, under the Candidates section. There is no separate save step — run a deep search and it is there. From the list you can re-open a past search, rename it, or delete it.

## What a saved search holds

A saved search is the **query**, not a frozen list of people.

When you open a saved search, Chosen shows you the results from when that search last ran. To get a current answer, you run the query again — and a fresh run scores against whoever is in your candidate pool *now*. New candidates who match will show up; candidates who no longer fit may drop. The query is the durable thing; the result set is whatever the most recent run produced.

That is the point of a saved search. It is the "people I should re-check every Monday" tool — the query stays the same, the answer moves as your pool changes.

<Callout type="info" title="A saved search is not a frozen set">
If you want a fixed group of people — the same candidates every time, regardless of who applies later — that is a list, not a search. Build it from the candidates table by selecting candidates and saving them. A list locks specific people in place. A saved search re-runs a question. Reach for whichever one matches what you actually need.
</Callout>

## Re-open a saved search

<Steps>
  <li>Find the search in the sidebar list under Candidates. It is titled by Chosen's reading of your query, or by whatever you renamed it to.</li>
  <li>Click it. The results from that search's last run load into the table.</li>
</Steps>

To get a fresh answer, run the same query again from the query bar in AI mode. The new run scores your current candidate pool and saves as its own entry.

## Rename a saved search

Chosen titles each search from its query, which is fine until you have a dozen of them. A clear name helps.

<Steps>
  <li>Hover the search in the sidebar list and open its menu — the three-dot button on the right.</li>
  <li>Choose **Rename**, type a name, and press Enter.</li>
</Steps>

## Delete a saved search

<Steps>
  <li>Open the same three-dot menu on the search.</li>
  <li>Choose **Delete** and confirm.</li>
</Steps>

<Callout type="warning" title="Deleting a search is permanent">
Delete removes the saved search and its stored results. There is no undo. It does not touch the candidates themselves — they stay in your system, in their jobs, exactly as they were. You are only removing the search, not the people it found.
</Callout>

## Limits

The sidebar shows your recent searches, not an unbounded archive — old ones age out of the list over time. A saved search re-runs against candidates already in Chosen; it does not reach the open web. For more on what each search mode does, see [smart, boolean, and deep search](/guides/search/smart-vs-deep).
